import React, { useEffect, useState } from 'react';
import 'dayjs/locale/uk';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCovenants,
  selectCovenantsError,
  selectCovenantsPageConfiguration,
  selectCovenantsStatus,
} from '~/store/covenants/selectors.ts';
import type { TApiRequiredDocument, TPageConfiguration } from '~/modules/covenants/types.ts';
import { AppDispatch } from '~/store/store.ts';
import { fetchDocumentCovenants } from '~/store/covenants/thunks/documentThunks.ts';
import { resetCovenants } from '~/store/covenants/slice.ts';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import { dataFormStyles } from '~/modules/covenants/CovenantsDataForm/styles.ts';
import {
  CovenantsDialog,
  COVENANTS_PER_PAGE,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/components/CovenantsDialog.tsx';
import { DocumentsViewTableHeader } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByClient/components/DocumentsViewTableHeader.tsx';
import { buildColumns } from '~/modules/covenants/constants.tsx';
import { TablePaginationControls } from '~/components/TablePaginationControls/TablePaginationControls.tsx';

interface IDocumentsViewProps {
  contragentId: number;
  documents: TApiRequiredDocument[];
  page: number;
  rowsPerPage: number;
  pageConfiguration: TPageConfiguration;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onCreateApplication: () => void;
}

const DocumentsView: React.FC<IDocumentsViewProps> = ({
  contragentId,
  documents,
  page,
  rowsPerPage,
  pageConfiguration,
  isLoading,
  onPageChange,
  onRowsPerPageChange,
  onCreateApplication,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [selectedDoc, setSelectedDoc] = useState<TApiRequiredDocument | null>(null);
  const [covenantsOpen, setCovenantsOpen] = useState(false);

  const covenants = useSelector(selectCovenants);
  const covenantsPageConfiguration = useSelector(selectCovenantsPageConfiguration);
  const covenantsStatus = useSelector(selectCovenantsStatus);
  const covenantsError = useSelector(selectCovenantsError);

  const covenantsLoading = covenantsStatus === 'loading';

  const handleOpenCovenants = (doc: TApiRequiredDocument) => {
    setSelectedDoc(doc);
    setCovenantsOpen(true);
    dispatch(
      fetchDocumentCovenants({
        contragentId,
        documentId: doc.id,
        yearPeriod: doc.yearPeriod,
        childPeriod: doc.childPeriod,
        deadline: doc.deadline || null,
        page: 0,
        size: COVENANTS_PER_PAGE,
      })
    );
  };

  const handleCloseCovenants = () => {
    setCovenantsOpen(false);
    dispatch(resetCovenants());
  };

  const handleCovenantsPageChange = (nextPage: number) => {
    if (!selectedDoc) {
      return;
    }

    dispatch(
      fetchDocumentCovenants({
        contragentId,
        documentId: selectedDoc.id,
        yearPeriod: selectedDoc.yearPeriod,
        childPeriod: selectedDoc.childPeriod,
        deadline: selectedDoc.deadline || null,
        page: nextPage,
        size: COVENANTS_PER_PAGE,
      })
    );
  };

  useEffect(() => {
    return () => {
      dispatch(resetCovenants());
    };
  }, [dispatch]);

  const columns = buildColumns(handleOpenCovenants);

  return (
    <Box>
      <Paper elevation={0} sx={dataFormStyles.documentTableSection}>
        <Box sx={dataFormStyles.documentTableToolbar}>
          <Typography variant="h6" sx={dataFormStyles.documentTableToolbarTitle}>
            Необхідні документи
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={onCreateApplication}
            sx={dataFormStyles.documentPrimaryAction}
          >
            Подати документ(и)
          </Button>
        </Box>

        <Box sx={commonStyles.loadingWrap}>
          <TableContainer sx={dataFormStyles.documentTableContainer}>
            <Table sx={dataFormStyles.documentTable}>
              <TableHead sx={dataFormStyles.documentTableHead}>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      sx={(theme) => dataFormStyles.documentTableHeadCell(theme, col.key)}
                    >
                      <DocumentsViewTableHeader columnKey={col.key} label={col.header} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!documents.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      align="center"
                      sx={dataFormStyles.documentEmptyCell}
                    >
                      Немає доступних документів за вибраний період
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((row, index) => (
                    <TableRow
                      key={`${row.id}-${row.yearPeriod}-${row.childPeriod}-${row.deadline || 'no-deadline'}-${row.status}-${index}`}
                      hover
                      sx={dataFormStyles.documentTableRow}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          sx={(theme) => dataFormStyles.documentTableBodyCell(theme, col.key)}
                        >
                          {col.render(row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {isLoading && (
            <Box sx={commonStyles.loadingMask}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>

        <TablePaginationControls
          page={page}
          rowsPerPage={rowsPerPage}
          totalElements={pageConfiguration.totalElements}
          isLoading={isLoading}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </Paper>

      <CovenantsDialog
        open={covenantsOpen}
        onClose={handleCloseCovenants}
        document={selectedDoc}
        covenants={covenants}
        covenantsTotalElements={covenantsPageConfiguration.totalElements}
        isLoading={covenantsLoading}
        error={covenantsError}
        onPageChange={handleCovenantsPageChange}
      />
    </Box>
  );
};

export default DocumentsView;
